import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const ClinicSchema = z.object({
  Name: z.string(),
  BillingStreet: z.string().nullable(),
  BillingCity: z.string().nullable(),
  BillingState: z.string().nullable(),
  BillingPostalCode: z.string().nullable(),
  BillingCountry: z.string().nullable(),
  Phone: z.string().nullable(),
  Website: z.string().nullable(),
  API_Id__c: z.string(),
  ClinicImageUrl__c: z.string().nullable(),
  Geolocation__c: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  distanceInMetres: z.number().optional(),
  Id: z.string(),
  partnerLevel: z.number(),
  partnerLevelTitle: z.string(),
  partnerLevelScore: z.any().transform((val) => (isNaN(val) ? 0 : Number(val))),
  AccountContactRelations: z
    .object({
      records: z.array(
        z.object({
          Contact: z.object({
            Salutation: z.string().nullable(),
            Name: z.string(),
          }),
        })
      ),
    })
    .nullable()
    .optional(),
});

export type IClinic = z.infer<typeof ClinicSchema>;

export function useClinics() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['clinics'],
    queryFn: fetchActiveClinics,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  return {
    clinics: data ?? [],
    isError,
    isPending,
  };
}

const BASE_URL = import.meta.env.VITE_BASE_URL;

async function fetchActiveClinics(): Promise<IClinic[]> {
  const isLocalhost = window.location.hostname === 'localhost';
  const origin = isLocalhost ? `https://clinic-locator.dev-allurion.com/api` : `${BASE_URL}/api`;

  try {
    const initialResponse = await fetch(`${origin}/clinics`);

    const data = await initialResponse.json();

    const validClinics: IClinic[] = [];
    const invalidClinics: { clinic: any; error: any }[] = [];

    for (const clinic of data) {
      const result = ClinicSchema.safeParse(clinic);

      if (result.success) {
        validClinics.push(result.data);
      } else {
        invalidClinics.push({ clinic, error: result.error.issues });
      }
    }

    if (invalidClinics.length > 0) {
      Sentry.captureMessage('Invalid clinics found', {
        level: 'warning',
        extra: {
          invalidClinicsLength: invalidClinics.length,
          invalidClinicsSample: invalidClinics.slice(0, 5), // include a sample of invalid clinics for debugging
        },
      });
    }

    return validClinics;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching active clinics:', error);

    return [];
  }
}
